import { memo, useMemo } from 'react';
import styles from './SelectorMenu.module.scss';

import clx from 'classnames';
import AvatarIcon from '../../AvatarIcon';
import { ReactComponent as Verified } from '../../../../assets/svg/verified.svg';
import { Minus, Plus } from 'lucide-react';

import clsx from 'clsx';

const toSelectedList = (value) => {
  if (Array.isArray(value)) return value;
  if (value != null && value !== '') return [value];
  return [];
};

const SelectorUserCard = memo(({ option }) => (
  <div className={styles.option_value_wrapper}>
    {option?.background ? (
      <div style={{ background: option.background }} className={styles.value_color} />
    ) : null}
    <div className={clsx(styles.section_value, styles.section_value_phone)}>
      <div className={styles.option_value}>{option.name}</div>
      <div className={styles.second_option}>
        {option.phone}
        <div style={{ alignItems: 'center' }}>
          {option.verified ? <Verified width={'13px'} height={'13px'} /> : null}
        </div>
      </div>
      {option?.email ? <div className={styles.second_option}>{option.email}</div> : null}
    </div>
  </div>
));

SelectorUserCard.displayName = 'SelectorUserCard';

const SelectorMenu = ({
  value = [],
  setValue,
  options,
  isMultiple,
  setIsDropdownOpen,
  fullHeight = false,
  border = true,
  error = false,
  blockedValues = [],
  width = '100%',
  isMultipleOne = false,
  isEmptyMessage = 'Nessuna opzione disponibili',
  darkMode = false,
  compact = false,
  number = false,
}) => {
  const selectedValues = useMemo(() => toSelectedList(value), [value]);
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
  const blockedSet = useMemo(() => new Set(blockedValues || []), [blockedValues]);
  const list = options || [];

  const selectValueHandler = (selectedValue) => {
    if (isMultiple) {
      if (selectedSet.has(selectedValue)) {
        setValue(selectedValues.filter((item) => item !== selectedValue));
      } else {
        setValue([...selectedValues, selectedValue]);
      }
      return;
    }

    // isMultipleOne: scelta singola; secondo tap sulla stessa voce la deseleziona (campo opzionale)
    if (isMultipleOne) {
      setValue(selectedSet.has(selectedValue) ? '' : selectedValue);
      return;
    }

    setValue(selectedValue);
    setIsDropdownOpen?.();
  };

  return (
    <div
      className={clx(styles.menu_wrapper, {
        [styles.menu_wrapper_fullHeight]: fullHeight,
        [styles.menu_wrapper_border]: !border,
        [styles.menu_wrapper_dark_mode]: darkMode,
        [styles.menu_wrapper_compact]: compact,
      })}
      style={{ width }}
      role={isMultipleOne ? 'radiogroup' : isMultiple ? 'group' : undefined}
    >
      {!list.length && (
        <div className={clx(styles.option_item)}>
          <div className={styles.option_value_wrapper}>
            <div className={styles.option_value}>{isEmptyMessage}</div>
          </div>
        </div>
      )}
      {list.map((option) => {
        const isSelected = selectedSet.has(option.value);
        const isBlocked = blockedSet.has(option.value);
        const isDisabled = Boolean(option.disabled) || isBlocked;

        return (
          <div
            className={clx(styles.option_item, {
              [styles.option_item_active]: isSelected,
              [styles.option_item_blocked]: isBlocked,
              [styles.option_item_disabled]: option.disabled,
            })}
            key={option.value}
            role={isMultipleOne ? 'radio' : isMultiple ? 'checkbox' : 'option'}
            aria-checked={isMultiple || isMultipleOne ? isSelected : undefined}
            aria-disabled={isDisabled || undefined}
            tabIndex={isDisabled ? -1 : 0}
            onClick={(event) => {
              if (isDisabled) return;
              event.stopPropagation();
              selectValueHandler(option.value);
            }}
            onKeyDown={(event) => {
              if (isDisabled) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                selectValueHandler(option.value);
              }
            }}
            onMouseDown={(event) => {
              // stopPropagation: non far chiudere/riaprire wrapper esterni; niente preventDefault (rompe il tap)
              event.stopPropagation();
            }}
          >
            {!number ? (
              <div className={styles.option_value_wrapper}>
                {option.icon ? (
                  <div className={styles.value_icon}>
                    <AvatarIcon
                      icon={option.icon}
                      name={option.name}
                      size={option.size || '50px'}
                      lazy
                    />
                  </div>
                ) : null}
                {option.background ? (
                  <div style={{ background: option.background }} className={styles.value_color} />
                ) : null}
                <div className={styles.section_value}>
                  <div className={styles.option_value}>{option.name}</div>
                </div>
              </div>
            ) : (
              <SelectorUserCard option={option} />
            )}

            {isMultiple ? (
              <span
                className={clx(styles.toggle_button, {
                  [styles.toggle_button_selected]: isSelected,
                })}
                aria-hidden
              >
                {isSelected ? (
                  <Minus size={16} strokeWidth={2.5} aria-hidden />
                ) : (
                  <Plus size={16} strokeWidth={2.5} aria-hidden />
                )}
              </span>
            ) : null}

            {isMultipleOne ? (
              <span
                className={clx(styles.choice_mark, {
                  [styles.choice_mark_checked]: isSelected,
                })}
                aria-hidden
              />
            ) : null}
          </div>
        );
      })}
      {!!error && <div className={styles.error_message}>{error}</div>}
    </div>
  );
};

export default memo(SelectorMenu);
